import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'

import {
  getUser,
  listPermissions,
  listRoles,
  listUsers,
  type AdminUserListQuery,
} from '@/api/admin-api'
import { branchListQueryOptions } from '@/api/reference-api'

import { adminKeys } from './admin-keys'

/**
 * Reads over the administration module.
 *
 * Paging and filtering the staff directory are the server's job, the same as every other list in
 * this application: an institution's roster does not fit in a browser, and a row the caller may
 * not see must never be trimmed client-side from a page that already contains it.
 *
 * Roles and the permission catalogue are both small, whole-table reads with no filter of their own
 * — `listRoles` and `listPermissions` take no arguments, so each has exactly one query key.
 */

export const adminUserListQueryOptions = (query: AdminUserListQuery) =>
  queryOptions({
    queryKey: adminKeys.userList(query),
    queryFn: () => listUsers(query),
    // Keeps the previous page on screen while the next one, or a filter change, is still in
    // flight — a directory that blanks on every keystroke cannot be read while it is searched.
    placeholderData: keepPreviousData,
  })

export const adminUserQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: adminKeys.userDetail(userId),
    queryFn: () => getUser(userId),
    enabled: userId.trim().length > 0,
  })

/**
 * Every role, with its permissions and member count. Also what a role's own detail screen reads
 * its row from — see the note on `RoleListItem` in `admin-types.ts`.
 */
export const adminRolesQueryOptions = () =>
  queryOptions({
    queryKey: adminKeys.roles(),
    queryFn: () => listRoles(),
    // Membership and permissions change rarely enough, and are re-read on every mutation anyway,
    // that a short stale time is enough to stop two panels on the same page firing the request
    // twice on mount.
    staleTime: 30 * 1000,
  })

/** The permission catalogue, grouped by module — what a role's permission editor is built from. */
export const adminPermissionCatalogueQueryOptions = () =>
  queryOptions({
    queryKey: adminKeys.permissions(),
    queryFn: () => listPermissions(),
    // The catalogue changes only when the server ships a new permission, which is a deploy, not
    // something a session needs to notice while it is open.
    staleTime: 30 * 60 * 1000,
  })

export const useAdminUserList = (query: AdminUserListQuery) => useQuery(adminUserListQueryOptions(query))

export const useAdminUser = (userId: string) => useQuery(adminUserQueryOptions(userId))

export const useAdminRoles = () => useQuery(adminRolesQueryOptions())

export const useAdminPermissionCatalogue = () => useQuery(adminPermissionCatalogueQueryOptions())

/** Branches, for the pickers on the create-user and edit-profile forms. Reads the shared reference query. */
export const useAdminBranches = () => useQuery(branchListQueryOptions())
